import React, { useState } from 'react';
import useCreateCompanyStore from '../../stores/createCompanyStore';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

const FormPage = ({
  title,
  subtitle,
  inputType = 'text', // 'text', 'email', 'none', 'multiple'
  placeholder = '',
  value = '',
  onChange,
  onNext,
  onPrevious,
  showNext = true,
  showPrevious = true,
  nextButtonText = 'Continue',
  previousButtonText = 'Back',
  isValid = true,
  children, // For custom content
  // New props for multiple fields
  fields = [], // Array of field objects
  formData = {}, // Current form data
  onFieldChange, // Function to handle individual field changes
  fieldValidation = {}, // Object with validation status for each field
  // Props for progress bar
  currentStep: propCurrentStep,
  totalSteps: propTotalSteps,
  isNextDisabled = false,
  showHelp = false,
}) => {
  const createCompanyStore = useCreateCompanyStore();
  // Use props if provided, otherwise fall back to create company store
  const currentStep = propCurrentStep !== undefined ? propCurrentStep : createCompanyStore.currentStep;
  const totalSteps = propTotalSteps !== undefined ? propTotalSteps : createCompanyStore.totalSteps;
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleNext = () => {
    if (onNext && isValid) {
      onNext();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isValid && showNext) {
      handleNext();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg">
        {/* Progress bar */}
        <div className="mb-12">
          <div className="w-full bg-gray-200/60 rounded-full h-1">
            <div
              className="bg-violet-700 h-1 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="flex justify-between mt-3">
            <span className="text-xs font-medium text-gray-500">
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span className="text-xs font-medium text-gray-500">
              {Math.round(progressPercent)}%
            </span>
          </div>
        </div>

        {/* Main content */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 relative">
          {/* Help button */}
          {showHelp && (
            <button
              onClick={() => setHelpModalOpen(true)}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-violet-700 hover:bg-violet-50 rounded-full transition-all duration-200"
              title="Get help"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}

          {/* Title and subtitle */}
          <div className="mb-10 text-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb-3 leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-gray-600 text-base leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>

          {/* Single Input section */}
          {inputType !== 'none' && inputType !== 'multiple' && (
            <div className="mb-10">
              <div className="relative">
                <input
                  type={inputType}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder={placeholder}
                  className={`w-full px-4 py-4 text-base border border-gray-200 rounded-2xl transition-all duration-200 outline-none bg-white placeholder-gray-400 ${
                    isFocused
                      ? 'border-violet-700 shadow-sm'
                      : 'hover:border-gray-300'
                  } ${!isValid && inputValue ? 'border-red-400' : ''}`}
                  autoFocus
                />
                {!isValid && inputValue && (
                  <p className="text-red-500 text-sm mt-3 font-medium">
                    Please enter a valid {inputType === 'email' ? 'email address' : 'value'}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Multiple Fields section */}
          {inputType === 'multiple' && fields.length > 0 && (
            <div className="mb-10 space-y-6">
              {fields.map((field, index) => (
                <div key={field.key} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                  </label>
                  <div className="relative">
                    <input
                      type={field.type}
                      value={formData[field.key] || ''}
                      onChange={(e) => onFieldChange && onFieldChange(field.key, e.target.value)}
                      placeholder={field.placeholder}
                      className={`w-full px-4 py-4 text-base border border-gray-200 rounded-2xl transition-all duration-200 outline-none bg-white placeholder-gray-400 hover:border-gray-300 focus:border-violet-700 focus:shadow-sm ${
                        fieldValidation[field.key] === false && formData[field.key] ? 'border-red-400' : ''
                      }`}
                      autoFocus={index === 0}
                    />
                    {fieldValidation[field.key] === false && formData[field.key] && (
                      <p className="text-red-500 text-sm mt-3 font-medium">
                        Please enter a valid {field.type === 'email' ? 'email address' : field.label.toLowerCase()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Custom children content */}
          {children && (
            <div className="mb-10">
              {children}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between items-center pt-2">
            {showPrevious ? (
              <button
                onClick={onPrevious}
                className="px-6 py-3 text-gray-500 hover:text-gray-700 font-medium transition-colors duration-200 flex items-center group rounded-xl hover:bg-gray-50"
              >
                <svg
                  className="w-4 h-4 mr-2 transform group-hover:-translate-x-0.5 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {previousButtonText}
              </button>
            ) : (
              <div />
            )}

            {showNext && (
              <button
                onClick={handleNext}
                disabled={isNextDisabled || !isValid}
                className={`px-8 py-4 rounded-2xl font-semibold text-white transition-all duration-200 flex items-center group ${
                  !isNextDisabled && isValid
                    ? 'bg-violet-700 hover:bg-violet-800 shadow-sm hover:shadow-md'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                {nextButtonText}
                <svg
                  className="w-4 h-4 ml-2 transform group-hover:translate-x-0.5 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Help Modal */}
        <Dialog
          open={helpModalOpen}
          onClose={() => setHelpModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: '24px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle className="text-xl font-semibold text-gray-900 pb-2">
            Need Help?
          </DialogTitle>
          <DialogContent>
            <p className="text-gray-700 text-base leading-relaxed mb-4">
              If you're running into any issues at all during the integration process, please don't hesitate to reach out for assistance.
            </p>
            <div className="p-4 bg-violet-50 rounded-2xl border border-violet-100">
              <p className="text-violet-800 font-medium mb-2">Contact Support:</p>
              <p className="text-violet-700">
                Email: <a href="mailto:will@getrightwork.com" className="font-medium underline hover:text-violet-900">will@getrightwork.com</a>
              </p>
            </div>
            <p className="text-gray-600 text-sm mt-4">
              We're here to help make your Toast integration as smooth as possible!
            </p>
          </DialogContent>
          <DialogActions className="px-6 pb-4">
            <Button
              onClick={() => setHelpModalOpen(false)}
              variant="contained"
              sx={{
                backgroundColor: '#7c3aed',
                '&:hover': {
                  backgroundColor: '#6d28d9'
                },
                borderRadius: '16px',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 3,
                paddingY: 1.5
              }}
            >
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default FormPage;
